/**
 * Google OAuth 2.0 Integration via Google Identity Services
 */

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '963386075152-lq1gmj105dre00l8tjqlisd3iukba1vl.apps.googleusercontent.com';

/**
 * Loads the Google Identity Services SDK script dynamically if not already present.
 */
export const loadGoogleScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2 || window.google?.accounts?.id) {
      return resolve(window.google);
    }

    const existingScript = document.getElementById('google-gsi-script') || document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google));
      existingScript.addEventListener('error', (e) => reject(e));
      // Falls das Skript bereits geladen wurde, aber window.google verzögert ist:
      let checkCount = 0;
      const interval = setInterval(() => {
        if (window.google?.accounts) {
          clearInterval(interval);
          resolve(window.google);
        } else if (++checkCount > 20) {
          clearInterval(interval);
          resolve(window.google);
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      resolve(window.google);
    };
    script.onerror = (err) => {
      reject(err);
    };
    document.head.appendChild(script);
  });
};

const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Initiates the Google OAuth popup flow and returns verified user profile details.
 * Falls fetch von userinfo im Browser durch CORS/Netzwerk blockiert ist, wird id_token dekodiert oder ein Fallback genutzt.
 */
export const signInWithGooglePopup = async () => {
  await loadGoogleScript();

  if (!window.google?.accounts) {
    throw new Error('Google-Dienst konnte im Browser nicht geladen werden.');
  }

  return new Promise((resolve, reject) => {
    try {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'email profile openid',
        callback: async (tokenResponse) => {
          if (tokenResponse.error) {
            return reject(new Error(tokenResponse.error_description || tokenResponse.error));
          }

          const accessToken = tokenResponse.access_token;
          if (!accessToken) {
            return reject(new Error('Kein Zugriffs-Token von Google erhalten.'));
          }

          // 1. Versuche Google UserInfo Endpoint
          try {
            const res = await fetch(`https://www.googleapis.com/oauth2/v3/userinfo?access_token=${encodeURIComponent(accessToken)}`, {
              headers: {
                Accept: 'application/json',
              },
            });

            if (res.ok) {
              const profile = await res.json();
              if (profile.email) {
                return resolve({
                  socialId: profile.sub,
                  email: profile.email,
                  firstName: profile.given_name || profile.name?.split(' ')[0] || 'Google',
                  lastName: profile.family_name || profile.name?.split(' ').slice(1).join(' ') || 'User',
                  avatar: profile.picture || '🌐',
                });
              }
            }
          } catch (fetchErr) {
            console.warn('UserInfo fetch failed, trying tokeninfo fallback...', fetchErr);
          }

          // 2. Fallback: Google TokenInfo Endpoint (sehr robust und erlaubt immer CORS)
          try {
            const infoRes = await fetch(`https://oauth2.googleapis.com/tokeninfo?access_token=${encodeURIComponent(accessToken)}`);
            if (infoRes.ok) {
              const info = await infoRes.json();
              if (info.email) {
                const namePart = info.email.split('@')[0];
                return resolve({
                  socialId: info.sub || info.user_id,
                  email: info.email,
                  firstName: namePart ? (namePart.charAt(0).toUpperCase() + namePart.slice(1)) : 'Google',
                  lastName: 'User',
                  avatar: '🌐',
                });
              }
            }
          } catch (tokenInfoErr) {
            console.error('TokenInfo fetch error:', tokenInfoErr);
          }

          reject(new Error('Konnte Benutzerdaten von Google nicht abrufen.'));
        },
        error_callback: (err) => {
          reject(new Error(err?.message || 'Google-Anmeldefenster abgebrochen oder blockiert.'));
        }
      });

      client.requestAccessToken({ prompt: 'select_account' });
    } catch (err) {
      reject(err);
    }
  });
};
